import React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'gatsby'
import BackgroundImage from 'gatsby-background-image'
import Img from 'gatsby-image'
import SVG from 'react-inlinesvg'
import Eye from '../../assets/svg/hover.svg'

const StyledBlogPostHover = styled.div`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: 250ms;
  width: 100%;

  svg {
    transform: translateY(-22px);
    transition: 250ms;
  }
`

const StyledBlogPostCard = styled(({ customListing, noExcerpt, ...rest }) => (
  <article {...rest} />
))`
  -moz-backface-visibility: hidden;
  -moz-transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
  0 0 1px 0 rgba(10, 22, 70, 0.06);
  min-height: 500px;
  overflow: hidden;
  position: relative;
  transition: 250ms;

  &:hover {
    box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
      0 0 1px 0 rgba(10, 22, 70, 0.06), 0 28px 70px -7px rgba(5, 15, 54, 0.16);

      ${StyledBlogPostHover}{
          opacity:1;
          svg{
              transform:translateY(0);
          }
      }
  }

  ${({ noExcerpt }) =>
    noExcerpt &&
    css`
      min-height: 416px;

      ${StyledBlogPostTitle} {
        margin-bottom: 0;
      }
    `}

  ${({ wide }) =>
    wide &&
    css`
    ${({ theme }) => theme.media.desktop}{
      display: flex;
      height: 186px;
      min-height: initial;

      section {
        width: 251px;
      }

      main {
        height: 100%;
        min-height: initial;
        width: 100%;
      }

      ${StyledBlogExcerpt} {
        display: none;
      }

      ${StyledBlogPostTitle}{
        margin-bottom: 0;
        margin-top: 8px;
      }
    `}

    ${({ customListing }) =>
      customListing &&
      css`
        min-height: 432px;

        ${StyledBlogPostTitle} {
          margin-top: 0;
        }

        ${StyledBlogPostData} {
          height: initial;
          min-height: 216px;
        }
      `}
    }
`

const StyledBackgroundImage = styled(({ noExcerpt, ...rest }) => (
  <BackgroundImage {...rest} />
))`
  min-height: 224px;

  ${({ noExcerpt }) =>
    noExcerpt &&
    css`
      min-height: 184px;
    `}
`

const StyledBlogPostImage = styled.div`
  position: relative;
`

const StyledBlogPostData = styled(({ noExcerpt, ...rest }) => (
  <main {...rest} />
))`
  display: flex;
  flex-direction: column;
  height: 276px;
  justify-content: space-between;
  padding: 32px;

  ${({ noExcerpt }) =>
    noExcerpt &&
    css`
      min-height: 120px;
      padding: 24px;
    `}
`

const StyledCategoryBar = styled.ul``

const StyledCategoryPill = styled.li`
  background-color: ${({ color }) => color};
  border-radius: 500px;
  color: #fff;
  display: inline-block;
  font-size: 8px;
  font-weight: bold;
  letter-spacing: 1.78px;
  line-height: 1.5;
  padding: 2px 8px;
  text-transform: uppercase;
`

const StyledBlogPostTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 8px;
  margin-top: 16px;
  max-height: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledBlogExcerpt = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${({ theme }) => theme.colors.greyExcerptColor};
  display: -webkit-box;
  font-size: 0.875em;
  line-height: 1.71;
  max-height: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledBlogAuthor = styled.div`
  display: flex;
  padding-top: 16px;
`

const StyledBlogAuthorAvatar = styled(Img)`
  border-radius: 50%;
  height: 20px;
  width: 20px;
`

const StyledBlogAuthorAvatarIMG = styled.img`
  border-radius: 50%;
  height: 20px;
  width: 20px;
`

const StyledBlogAuthorData = styled.div`
  color: ${({ theme }) => theme.colors.greyParagraphColor};
  font-size: 0.75em;
  line-height: 1.67;
  margin-left: 8px;
`

const BlogPostCard = ({ data, wide, noExcerpt, customListing, blogSlider }) => {
  if (!data) {
    return null
  }

  return (
    <Link to={`/${data.slug}`}>
      <StyledBlogPostCard
        customListing={customListing}
        noExcerpt={noExcerpt}
        wide={wide}
      >
        <StyledBlogPostImage>
          {data.featuredImage && (
            <StyledBackgroundImage
              Tag="section"
              fluid={data.featuredImage.fluid}
              backgroundColor={`#fff`}
              noExcerpt={noExcerpt}
            />
          )}
          <StyledBlogPostHover>
            <SVG src={Eye} />
          </StyledBlogPostHover>
        </StyledBlogPostImage>
        <StyledBlogPostData noExcerpt={noExcerpt}>
          <div>
            {!customListing && (
              <StyledCategoryBar>
                <StyledCategoryPill color={data.category.color}>
                  {data.category.name}
                </StyledCategoryPill>
              </StyledCategoryBar>
            )}

            <StyledBlogPostTitle>
              {!blogSlider ? (
                <h2>{data.name}</h2>
              ) : (
                <p>{data.name}</p>
              )}
            </StyledBlogPostTitle>
            {noExcerpt ? null : (
              <StyledBlogExcerpt>{data.excerpt}</StyledBlogExcerpt>
            )}
          </div>
          {!customListing && (
            <div>
              <StyledBlogAuthor>
                {data.author.avatar &&
                  (data.author.avatar.fluid.src ? (
                    <StyledBlogAuthorAvatar fluid={data.author.avatar.fluid} />
                  ) : data.author.avatar.file ? (
                    <StyledBlogAuthorAvatarIMG
                      src={data.author.avatar.file.url}
                    />
                  ) : (
                    <StyledBlogAuthorAvatarIMG
                      src={require('../../assets/svg/shape-copy.svg')}
                    />
                  ))}
                <StyledBlogAuthorData>{data.author.name}</StyledBlogAuthorData>
                <StyledBlogAuthorData>
                  • {(!customListing && data.readTime) || 5} min read
                </StyledBlogAuthorData>
              </StyledBlogAuthor>
            </div>
          )}
        </StyledBlogPostData>
      </StyledBlogPostCard>
    </Link>
  )
}

export default BlogPostCard
